import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SearchBox,
  Text,
  Tooltip,
  mergeClasses,
} from "@fluentui/react-components";
import {
  Add20Filled,
  ArrowSortDownLinesRegular,
  ArrowSortUpLinesRegular,
} from "@fluentui/react-icons";
import {
  archiveWorkspace,
  deleteWorkspace,
  unArchiveWorkspace,
  updateWorkspace,
} from "api";
import { ConstantValues } from "common/constants";
import { useChatStateReset } from "hooks/useChatStateReset";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { IUserDetails } from "models/IUserDetails";
import { IWorkspace } from "models/IWorkspace";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { readSortingOptions, writeSortingOptions } from "utils/generalUtils";
import { WorkspaceIcon } from "../../icons/WorkspaceIcon";
import { setActiveWorkspace } from "../../redux/activeWorkspaceSlice";
import { setNewConfigForKey } from "../../redux/llmConfig";
import { showToast } from "../../redux/toastActions";
import {
  removeWorkspace,
  updateWorkspaceInSlice,
} from "../../redux/workspacesSlice";
import {
  useSharedButtonStyles,
  useSharedCardGridStyles,
  useSharedEmptyStateStyles,
  useSharedPageContainerStyles,
  useSharedPageHeaderStyles,
  useSharedPageSearchStyles,
  useSharedPillButtonStyles,
  useSharedPillTabContainerStyles,
  useSharedResponsiveSectionStyles,
  useSharedSortControlStyles,
} from "../../theme/sharedStyles";
import { AddDocumentsDialog } from "./addDocumentsDialog/AddDocumentsDialog";
import { AddMemberDialog } from "./addMemberDialog/AddMemberDialog";
import { EditWorkspaceDialog } from "./editDialog/EditWorkspaceDialog";
import {
  ARCHIVE_DIALOG,
  DELETE_DIALOG,
  DIALOG_LABELS,
  EMPTY_STATE_LABELS,
  LEAVE_DIALOG,
  PAGE_LABELS,
  SORT_FIELD_LABELS,
  SortField,
  TABS,
  TabValue,
  UNARCHIVE_DIALOG,
} from "./ManageWorkspacesCardView.types";
import { WorkspaceCard } from "./workspaceCard/WorkspaceCard";
import { ViewWorkspace } from "./viewDialog/ViewWorkspace";

const SORTING_SCOPE = "WorkspacesCardView";

type WorkspaceSortingOptions = {
  tab: TabValue;
  field: SortField;
  order: "asc" | "desc";
};

export const ManageWorkspacesCardView = (): JSX.Element => {
  const containerStyles = useSharedPageContainerStyles();
  const headerStyles = useSharedPageHeaderStyles();
  const sectionStyles = useSharedResponsiveSectionStyles();
  const pageSearchStyles = useSharedPageSearchStyles();
  const sortStyles = useSharedSortControlStyles();
  const gridStyles = useSharedCardGridStyles();
  const emptyStateStyles = useSharedEmptyStateStyles();
  const pillButtonStyles = useSharedPillButtonStyles();
  const pillTabContainerStyles = useSharedPillTabContainerStyles();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const workspaces = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const queryClient = useQueryClient();
  const resetChatState = useChatStateReset();
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );

  const savedSortingOptions =
    readSortingOptions<WorkspaceSortingOptions>(SORTING_SCOPE);
  const [activeTab, setActiveTab] = useState<TabValue>(
    savedSortingOptions?.tab ?? TABS.MY_WORKSPACES,
  );
  const [sortField, setSortField] = useState<SortField>(
    savedSortingOptions?.field ?? "name",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    savedSortingOptions?.order ?? "asc",
  );
  const [searchQuery, setSearchQuery] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingWorkspace, setEditingWorkspace] = useState<IWorkspace | null>(
    null,
  );
  const [viewingWorkspace, setViewingWorkspace] = useState<
    IWorkspace | undefined
  >(undefined);
  const [selectedWorkspace, setSelectedWorkspace] = useState<IWorkspace | null>(
    null,
  );
  const [showArchiveDialog, setShowArchiveDialog] = useState<boolean>(false);
  const [showUnarchiveDialog, setShowUnarchiveDialog] =
    useState<boolean>(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState<boolean>(false);
  const [addDocumentsWorkspace, setAddDocumentsWorkspace] =
    useState<IWorkspace | null>(null);
  const [addMemberWorkspace, setAddMemberWorkspace] =
    useState<IWorkspace | null>(null);

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const createWorkspaceButtonRef = useRef<HTMLButtonElement>(null);
  const TAB_VALUES: TabValue[] = [
    TABS.MY_WORKSPACES,
    TABS.SHARED_WITH_ME,
    TABS.ARCHIVED,
  ];

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = TAB_VALUES.indexOf(activeTab);
      let nextIndex: number | null = null;

      if (e.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % TAB_VALUES.length;
      } else if (e.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + TAB_VALUES.length) % TAB_VALUES.length;
      }

      if (nextIndex !== null) {
        e.preventDefault();
        setActiveTab(TAB_VALUES[nextIndex]);
        tabRefs.current[nextIndex]?.focus();
      }
    },
    [activeTab],
  );

  useEffect(() => {
    writeSortingOptions(SORTING_SCOPE, {
      tab: activeTab,
      field: sortField,
      order: sortOrder,
    });
  }, [activeTab, sortField, sortOrder]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const openCreateDialog = () => {
    setEditingWorkspace(null);
    setDialogOpen(true);
  };

  const openEditDialog = (workspace: IWorkspace) => {
    setEditingWorkspace(workspace);
    setDialogOpen(true);
  };

  const handleDelete = (workspace: IWorkspace) => {
    setShowDeleteDialog(true);
    setSelectedWorkspace(workspace);
  };

  const onDeleteConfirmed = async (): Promise<void> => {
    if (!selectedWorkspace) return;

    try {
      await deleteWorkspace({
        workspaceId: selectedWorkspace.id ?? ConstantValues.EMPTY_STRING,
      });
      dispatch(
        removeWorkspace(selectedWorkspace.id ?? ConstantValues.EMPTY_STRING),
      );

      if (activeWorkspace?.id === selectedWorkspace.id) {
        const personalWorkspace = workspaces.find(
          (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
        );
        if (personalWorkspace) {
          dispatch(setActiveWorkspace(personalWorkspace));
          dispatch(
            setNewConfigForKey({
              key: ConstantValues.CHAT,
              config: {
                rag: false,
                citate: false,
                sourceRagCategories: [],
              },
            }),
          );
        }
      }

      setSelectedWorkspace(null);
    } catch (error) {
      console.error("Error deleting workspace:", error);
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleOnEnable = async (workspace: IWorkspace) => {
    resetChatState();
    dispatch(setActiveWorkspace(workspace));
    localStorage.setItem(
      ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
      workspace.id,
    );
    await queryClient.invalidateQueries(["conversations"]);
    navigate("/");
  };

  const handleArchive = async (workspace: IWorkspace) => {
    if (workspace.archived) return;

    setShowArchiveDialog(true);
    setSelectedWorkspace(workspace);
  };

  const onLeave = async (workspace: IWorkspace) => {
    setSelectedWorkspace(workspace);
    setShowLeaveDialog(true);
  };

  const handleUnarchive = async (workspace: IWorkspace) => {
    if (!workspace.archived) return;

    setShowUnarchiveDialog(true);
    setSelectedWorkspace(workspace);
  };

  const onArchiveConfirmed = async (): Promise<void> => {
    if (!selectedWorkspace?.id) return;

    try {
      await archiveWorkspace({ id: selectedWorkspace.id });

      const resultWorkspace: IWorkspace = {
        ...selectedWorkspace,
        archived: true,
        id: selectedWorkspace.id,
        updatedAt: new Date().toISOString(),
      };

      dispatch(updateWorkspaceInSlice(resultWorkspace));

      if (activeWorkspace?.id === selectedWorkspace.id) {
        const personalWorkspace = workspaces.find(
          (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
        );
        if (personalWorkspace) {
          dispatch(setActiveWorkspace(personalWorkspace));
          dispatch(
            setNewConfigForKey({
              key: ConstantValues.CHAT,
              config: {
                rag: false,
                citate: false,
                sourceRagCategories: [],
              },
            }),
          );
        }
      }
    } catch (error) {
      console.error("Failed to archive workspace:", error);
      dispatch(showToast("Failed to archive workspace", "error"));
    } finally {
      setShowArchiveDialog(false);
    }
  };

  const onUnarchiveConfirmed = async (): Promise<void> => {
    if (!selectedWorkspace?.id) return;

    try {
      await unArchiveWorkspace({ id: selectedWorkspace.id });

      const resultWorkspace: IWorkspace = {
        ...selectedWorkspace,
        archived: false,
        id: selectedWorkspace.id,
        updatedAt: new Date().toISOString(),
      };

      dispatch(updateWorkspaceInSlice(resultWorkspace));
    } catch (error) {
      console.error("Failed to unarchive workspace:", error);
      dispatch(showToast("Failed to unarchive workspace", "error"));
    } finally {
      setShowUnarchiveDialog(false);
    }
  };

  const onLeaveConfirmed = async () => {
    if (!selectedWorkspace?.id) return;

    try {
      const updatedMembers: IUserDetails[] =
        selectedWorkspace.teamMembers?.filter(
          (m) => m.userId !== currentUser.id,
        ) ?? [];

      const updatedWorkspace: IWorkspace = {
        ...selectedWorkspace,
        teamMembers: updatedMembers,
      };

      await updateWorkspace({
        id: selectedWorkspace.id,
        workspace: updatedWorkspace,
      });
      dispatch(removeWorkspace(selectedWorkspace.id));

      if (activeWorkspace?.id === selectedWorkspace.id) {
        const personalWorkspace = workspaces.find(
          (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
        );
        if (!personalWorkspace) return;

        dispatch(setActiveWorkspace(personalWorkspace));
        dispatch(
          setNewConfigForKey({
            key: ConstantValues.CHAT,
            config: {
              rag: false,
              citate: false,
              sourceRagCategories: [],
            },
          }),
        );
      }
    } catch (error) {
      console.error("Error leaving workspace:", error);
    } finally {
      setShowLeaveDialog(false);
    }
  };

  const filteredWorkspaces = useMemo(() => {
    return workspaces.filter((workspace: IWorkspace) => {
      let matchesTab = false;
      const isOwner =
        workspace.owners?.some((o) => o.userId === currentUser?.id) ?? false;
      const isPersonalWorkspace =
        workspace.name === ConstantValues.PERSONAL_WORKSPACE;

      if (activeTab === TABS.MY_WORKSPACES) {
        matchesTab = !workspace.archived && (isOwner || isPersonalWorkspace);
      } else if (activeTab === TABS.SHARED_WITH_ME) {
        matchesTab =
          !workspace.archived &&
          !isOwner &&
          !isPersonalWorkspace &&
          (workspace.teamMembers?.some((m) => m.userId === currentUser?.id) ??
            false);
      } else if (activeTab === TABS.ARCHIVED) {
        matchesTab = !isPersonalWorkspace && (workspace.archived ?? false);
      }

      if (!matchesTab) return false;

      if (!debouncedSearchQuery.trim()) return true;

      const query = debouncedSearchQuery.toLowerCase();
      const nameMatches = workspace.name?.toLowerCase().includes(query);
      const descriptionMatches = workspace.clientName
        ?.toLowerCase()
        .includes(query);

      return nameMatches || descriptionMatches;
    });
  }, [workspaces, activeTab, currentUser, debouncedSearchQuery]);

  const sortedWorkspaces = useMemo(() => {
    const sorted = [...filteredWorkspaces];
    const isPersonal = (ws: IWorkspace) =>
      ws.name === ConstantValues.PERSONAL_WORKSPACE;
    const dir = sortOrder === "asc" ? 1 : -1;

    return sorted.sort((a, b) => {
      if (isPersonal(a) && !isPersonal(b)) return -1;
      if (!isPersonal(a) && isPersonal(b)) return 1;

      switch (sortField) {
        case "updatedAt": {
          const dateA =
            new Date(
              a.updatedAt || a.createdAt || ConstantValues.EMPTY_STRING,
            ).getTime() || 0;
          const dateB =
            new Date(
              b.updatedAt || b.createdAt || ConstantValues.EMPTY_STRING,
            ).getTime() || 0;
          return (dateA - dateB) * dir;
        }
        case "createdAt": {
          const dateA =
            new Date(a.createdAt || ConstantValues.EMPTY_STRING).getTime() || 0;
          const dateB =
            new Date(b.createdAt || ConstantValues.EMPTY_STRING).getTime() || 0;
          return (dateA - dateB) * dir;
        }
        case "name":
        default:
          return a.name.localeCompare(b.name) * dir;
      }
    });
  }, [filteredWorkspaces, sortField, sortOrder]);

  const handleAddDocuments = (workspace: IWorkspace) => {
    setAddDocumentsWorkspace(workspace);
  };

  const handleAddMember = (workspace: IWorkspace) => {
    setAddMemberWorkspace(workspace);
  };

  const getEmptyStateMessage = (): string => {
    if (debouncedSearchQuery.trim()) {
      return `${EMPTY_STATE_LABELS.NO_SEARCH_RESULTS} ${EMPTY_STATE_LABELS.NO_SEARCH_RESULTS_SUBTITLE}`;
    }
    switch (activeTab) {
      case TABS.MY_WORKSPACES:
        return `${EMPTY_STATE_LABELS.NO_WORKSPACES} ${EMPTY_STATE_LABELS.MY_WORKSPACES_SUBTITLE}`;
      case TABS.SHARED_WITH_ME:
        return EMPTY_STATE_LABELS.SHARED_WITH_ME_SUBTITLE;
      case TABS.ARCHIVED:
        return EMPTY_STATE_LABELS.ARCHIVED_SUBTITLE;
      default:
        return `${EMPTY_STATE_LABELS.NO_WORKSPACES} ${EMPTY_STATE_LABELS.MY_WORKSPACES_SUBTITLE}`;
    }
  };

  return (
    <>
      <div className={containerStyles.container}>
        <div className={headerStyles.header}>
          <h1 className={headerStyles.title}>{PAGE_LABELS.TITLE}</h1>
          <Button
            ref={createWorkspaceButtonRef}
            className={headerStyles.createButton}
            onClick={openCreateDialog}
            icon={<Add20Filled />}
          >
            {PAGE_LABELS.CREATE_WORKSPACE}
          </Button>
        </div>

        <div className={sectionStyles.root}>
          <SearchBox
            className={pageSearchStyles.searchInput}
            placeholder="Search workspaces by title, description..."
            aria-label="Search workspaces"
            value={searchQuery}
            onChange={(_, data) =>
              setSearchQuery(data?.value || ConstantValues.EMPTY_STRING)
            }
          />
        </div>

        <div className={sortStyles.container}>
          <div
            className={pillTabContainerStyles.tabList}
            role="tablist"
            aria-label="Workspace filters"
          >
            <button
              type="button"
              ref={(el) => {
                tabRefs.current[0] = el;
              }}
              role="tab"
              id="tab-my-workspaces"
              aria-selected={activeTab === TABS.MY_WORKSPACES}
              aria-controls="tabpanel-workspaces"
              tabIndex={activeTab === TABS.MY_WORKSPACES ? 0 : -1}
              onClick={() => setActiveTab(TABS.MY_WORKSPACES)}
              onKeyDown={handleTabKeyDown}
              className={mergeClasses(
                pillButtonStyles.pillButton,
                activeTab === TABS.MY_WORKSPACES &&
                  pillButtonStyles.pillButtonActive,
              )}
            >
              {TABS.MY_WORKSPACES}
            </button>
            <button
              type="button"
              ref={(el) => {
                tabRefs.current[1] = el;
              }}
              role="tab"
              id="tab-shared-with-me"
              aria-selected={activeTab === TABS.SHARED_WITH_ME}
              aria-controls="tabpanel-workspaces"
              tabIndex={activeTab === TABS.SHARED_WITH_ME ? 0 : -1}
              onClick={() => setActiveTab(TABS.SHARED_WITH_ME)}
              onKeyDown={handleTabKeyDown}
              className={mergeClasses(
                pillButtonStyles.pillButton,
                activeTab === TABS.SHARED_WITH_ME &&
                  pillButtonStyles.pillButtonActive,
              )}
            >
              {TABS.SHARED_WITH_ME}
            </button>
            <button
              type="button"
              ref={(el) => {
                tabRefs.current[2] = el;
              }}
              role="tab"
              id="tab-archived"
              aria-selected={activeTab === TABS.ARCHIVED}
              aria-controls="tabpanel-workspaces"
              tabIndex={activeTab === TABS.ARCHIVED ? 0 : -1}
              onClick={() => setActiveTab(TABS.ARCHIVED)}
              onKeyDown={handleTabKeyDown}
              className={mergeClasses(
                pillButtonStyles.pillButton,
                activeTab === TABS.ARCHIVED &&
                  pillButtonStyles.pillButtonActive,
              )}
            >
              {TABS.ARCHIVED}
            </button>
          </div>

          <div className={sortStyles.sortGroup}>
            <span
              id="sort-by-label-workspaces"
              className={sortStyles.sortLabel}
            >
              Sort by:
            </span>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuButton
                  appearance="outline"
                  className={sortStyles.sortMenuButton}
                  aria-labelledby="sort-by-label-workspaces"
                >
                  {SORT_FIELD_LABELS[sortField]}
                </MenuButton>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem onClick={() => setSortField("name")}>Name</MenuItem>
                  <MenuItem onClick={() => setSortField("updatedAt")}>
                    Last Updated
                  </MenuItem>
                  <MenuItem onClick={() => setSortField("createdAt")}>
                    Date Created
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
            <Tooltip
              content={
                sortOrder === "asc"
                  ? "Currently ascending, sort descending"
                  : "Currently descending, sort ascending"
              }
              relationship="label"
              appearance="inverted"
            >
              <Button
                appearance="subtle"
                icon={
                  sortOrder === "asc" ? (
                    <ArrowSortDownLinesRegular />
                  ) : (
                    <ArrowSortUpLinesRegular />
                  )
                }
                className={sortStyles.sortDirectionButton}
                onClick={() =>
                  setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
                }
                aria-label={
                  sortOrder === "asc"
                    ? "Currently ascending, sort descending"
                    : "Currently descending, sort ascending"
                }
              />
            </Tooltip>
          </div>
        </div>

        <div
          id="tabpanel-workspaces"
          role="tabpanel"
          aria-labelledby={`tab-${activeTab === TABS.MY_WORKSPACES ? "my-workspaces" : activeTab === TABS.SHARED_WITH_ME ? "shared-with-me" : "archived"}`}
          className={gridStyles.gridScroll}
        >
          {sortedWorkspaces.length > 0 ? (
            <div className={gridStyles.gridContainer}>
              <div className={gridStyles.grid}>
                {sortedWorkspaces.map((workspace: IWorkspace) => (
                  <WorkspaceCard
                    key={workspace.id}
                    workspace={workspace}
                    onEdit={openEditDialog}
                    onDelete={handleDelete}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                    onLeave={onLeave}
                    onEnable={handleOnEnable}
                    onSeeDetails={() => setViewingWorkspace(workspace)}
                    onAddDocuments={handleAddDocuments}
                    onAddMember={handleAddMember}
                    isActive={workspace.id === activeWorkspace?.id}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className={emptyStateStyles.container}>
              <Text size={300} weight="regular">
                {getEmptyStateMessage()}
              </Text>
            </div>
          )}
        </div>

        {viewingWorkspace && (
          <ViewWorkspace
            isOpen={true}
            workspace={viewingWorkspace}
            onDismiss={() => {
              setViewingWorkspace(undefined);
              setSelectedWorkspace(null);
            }}
          />
        )}
      </div>

      {showDeleteDialog && (
        <ActionDialog
          isVisible={showDeleteDialog}
          title={DELETE_DIALOG.TITLE}
          onConfirmation={onDeleteConfirmed}
          onDismiss={() => {
            setShowDeleteDialog(false);
            setSelectedWorkspace(null);
          }}
          confirmationMessage="Delete"
          dismissMessage="Cancel"
          message={
            <>
              Are you sure you want to delete the "{selectedWorkspace?.name}"
              workspace?
              <br />
              All conversations and documents within this workspace will be
              deleted.
            </>
          }
        />
      )}

      {dialogOpen && (
        <EditWorkspaceDialog
          isOpen={dialogOpen}
          onDismiss={() => {
            setDialogOpen(false);
            createWorkspaceButtonRef.current?.focus();
          }}
          workspace={editingWorkspace}
          allowOwnerEdit={true}
        />
      )}

      {showArchiveDialog && (
        <ActionDialog
          isVisible={showArchiveDialog}
          title={ARCHIVE_DIALOG.TITLE}
          onConfirmation={onArchiveConfirmed}
          onDismiss={() => {
            setShowArchiveDialog(false);
            setSelectedWorkspace(null);
          }}
          confirmationMessage={DIALOG_LABELS.YES}
          dismissMessage={DIALOG_LABELS.NO}
          spinnerMessage={ARCHIVE_DIALOG.SPINNER}
          message={ARCHIVE_DIALOG.MESSAGE}
        />
      )}

      {showUnarchiveDialog && (
        <ActionDialog
          isVisible={showUnarchiveDialog}
          title={UNARCHIVE_DIALOG.TITLE}
          onConfirmation={onUnarchiveConfirmed}
          onDismiss={() => {
            setShowUnarchiveDialog(false);
            setSelectedWorkspace(null);
          }}
          confirmationMessage={DIALOG_LABELS.YES}
          dismissMessage={DIALOG_LABELS.NO}
          spinnerMessage={UNARCHIVE_DIALOG.SPINNER}
          message={UNARCHIVE_DIALOG.MESSAGE}
        />
      )}

      {showLeaveDialog && (
        <ActionDialog
          isVisible={showLeaveDialog}
          title={LEAVE_DIALOG.TITLE}
          onConfirmation={onLeaveConfirmed}
          onDismiss={() => {
            setShowLeaveDialog(false);
            setSelectedWorkspace(null);
          }}
          confirmationMessage={DIALOG_LABELS.YES}
          dismissMessage={DIALOG_LABELS.NO}
          spinnerMessage={LEAVE_DIALOG.SPINNER}
          message={LEAVE_DIALOG.MESSAGE}
        />
      )}

      {addDocumentsWorkspace && (
        <AddDocumentsDialog
          isOpen={!!addDocumentsWorkspace}
          workspace={addDocumentsWorkspace}
          onClose={() => {
            setAddDocumentsWorkspace(null);
            setSelectedWorkspace(null);
          }}
          canDelete={
            addDocumentsWorkspace.owners?.some(
              (o) => o.userId === currentUser.id,
            ) ?? false
          }
        />
      )}

      {addMemberWorkspace && (
        <AddMemberDialog
          isOpen={!!addMemberWorkspace}
          workspace={addMemberWorkspace}
          onDismiss={() => {
            setAddMemberWorkspace(null);
            setSelectedWorkspace(null);
          }}
          canRemoveExisting={
            addMemberWorkspace.owners?.some(
              (o) => o.userId === currentUser.id,
            ) ?? false
          }
        />
      )}
    </>
  );
};
